import React, { useState, useEffect } from 'react';
import { Table, Button, Input, message, Form, Space } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import './index.less';
import dayjs from 'dayjs';

interface BackroomsTeamCardInfo {
  id: number;
  teamName: string;
  englishName: string;
  description: string;
  image_url: string;
  thumbnailUrl: string;
}

interface TeamCard {
  id: number;
  teamName: string;
  englishName: string;
  isFriendly: string;
  rarity: string;
  totalId: number;
  description: string;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl: string;
  logo: string;
}

interface BackroomsTeamCardAddress {
  hash: string;
  teamCard: TeamCard;
  customName: string | null;
  lastCheckIn: string | null;
  id: number;
  experience: number;
  contributionValue: number;
  createdAt: string;
  updatedAt: string;
}

interface BackroomsTeamCardProps {
  // Add your props here
}

const BackroomsTeamCard: React.FC<BackroomsTeamCardProps> = () => {
  const [data, setData] = useState<BackroomsTeamCardInfo[]>([]);
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [bulkQuantity, setBulkQuantity] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [form] = Form.useForm<FormInstance>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch('/api/v1/backroomsteamcard/info');
      const result = await response.json();
      setData(result);
      // Initialize quantities
      const initialQuantities = result.reduce((acc: { [key: number]: number }, item: BackroomsTeamCardInfo) => {
        acc[item.id] = 0;
        return acc;
      }, {});
      setQuantities(initialQuantities);
    } catch (error) {
      message.error('获取数据失败');
    }
  };

  const handleQuantityChange = (id: number, value: number) => {
    setQuantities(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleBulkSubmit = async () => {
    const itemsToSubmit = Object.entries(quantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([id, quantity]) => ({
        teamCardId: parseInt(id),
        quantity
      }));

    if (itemsToSubmit.length === 0) {
      message.error('请至少选择一个项目并设置数量');
      return;
    }

    try {
      const response = await fetch('/api/v1/backroomsteamcard/address/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemsToSubmit),
      });

      const result = await response.json() as BackroomsTeamCardAddress[];
      
      // 按 teamCard.id 分组数据
      const groupedData = result.reduce((acc: { [key: number]: BackroomsTeamCardAddress[] }, item) => {
        const teamId = item.teamCard.id;
        if (!acc[teamId]) {
          acc[teamId] = [];
        }
        acc[teamId].push(item);
        return acc;
      }, {});

      // 为每个团队生成一个文本文件
      const timestamp = dayjs().format('MM/DD');
      Object.entries(groupedData).forEach(([teamId, items]) => {
        const name = items[0].teamCard.englishName;
        const quantity = quantities[parseInt(teamId)];
        const fileName = `${name}_${timestamp}_${quantity}.txt`;
        
        // 提取所有 hash 值并用换行符连接，最后添加一个换行符
        const content = items.map(item => `https://ds2.cn/bt/${item.hash}`).join('\r\n') + '\r\n';
        // 添加 UTF-8 BOM 标记
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, fileName);
      });

      message.success('提交成功');
    } catch (error) {
      message.error('提交失败');
    }
  };

  const handleCreateEvent = async (values: any) => {
    try {
      const response = await fetch('/api/v1/backroomsteamcard/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...values,
          startTime: new Date(values.startTime).toISOString(),
          endTime: new Date(values.endTime).toISOString(),
          allowedTeamIds: values.allowedTeamIds.split(',').map((id: string) => parseInt(id.trim())),
          availableAchievementIds: values.availableAchievementIds ? values.availableAchievementIds.split(',').map((id: string) => parseInt(id.trim())) : [],
        }),
      });

      if (!response.ok) {
        throw new Error('创建事件失败');
      }

      message.success('创建事件成功');
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || '创建事件失败');
    }
  };

  const handleBulkQuantityApply = () => {
    const newQuantities = data.reduce((acc: { [key: number]: number }, item: BackroomsTeamCardInfo) => {
      acc[item.id] = bulkQuantity;
      return acc;
    }, {});
    setQuantities(newQuantities);
  };

  const handleBulkQuantityApplyToCurrentPage = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageItems = data.slice(startIndex, endIndex);
    
    const newQuantities = { ...quantities };
    currentPageItems.forEach(item => {
      newQuantities[item.id] = bulkQuantity;
    });
    setQuantities(newQuantities);
  };

  const handleGenerateAllTeamCards = async () => {
    if (bulkQuantity <= 0) {
      message.warning('请先设置生成数量');
      return;
    }

    try {
      message.loading('正在生成全部团队卡...', 0);
      
      const response = await fetch('/api/v1/backroomsteamcard/address/generate-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity: bulkQuantity }),
      });

      if (!response.ok) {
        throw new Error('生成失败');
      }

      const result = await response.json();
      message.destroy();
      message.success('生成成功！');

      console.log('API返回的数据总数:', result.length);
      console.log('API返回的数据结构:', result[0]);

      // 按团队分组数据
      const groupedData: { [key: string]: any[] } = {};
      result.forEach((item: any) => {
        // 兼容不同的数据结构
        const teamId = item.teamCard?.id || item.teamCardId;
        const teamName = item.teamCard?.englishName || item.teamCard?.teamName;
        
        if (!groupedData[teamId]) {
          groupedData[teamId] = [];
        }
        groupedData[teamId].push({
          ...item,
          teamCard: {
            id: teamId,
            englishName: teamName
          }
        });
      });

      console.log('分组后的数据:', groupedData);
      console.log('分组后的团队数量:', Object.keys(groupedData).length);

      // 分批下载文件，避免浏览器限制
      const timestamp = dayjs().format('MM/DD');
      const entries = Object.entries(groupedData);
      const batchSize = 3; // 每批下载3个文件，减少并发
      
      message.info(`开始下载 ${entries.length} 个文件，每批 ${batchSize} 个...`);
      
      let downloadedCount = 0;
      
      for (let i = 0; i < entries.length; i += batchSize) {
        const batch = entries.slice(i, i + batchSize);
        
        // 延迟下载，避免浏览器阻塞
        setTimeout(() => {
          batch.forEach(([teamId, items]) => {
            const name = items[0].teamCard.englishName;
            const fileName = `${name}_${timestamp}_${bulkQuantity}.txt`;
            
            // 提取所有 hash 值并用换行符连接，最后添加一个换行符
            const content = items.map(item => `https://ds2.cn/bt/${item.hash}`).join('\r\n') + '\r\n';
            // 添加 UTF-8 BOM 标记
            const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
            const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
            saveAs(blob, fileName);
            
            downloadedCount++;
            console.log(`已下载 ${downloadedCount}/${entries.length} 个文件: ${fileName}`);
          });
          
          if (downloadedCount >= entries.length) {
            message.success(`所有 ${entries.length} 个文件下载完成！`);
          }
        }, i * 300); // 每批间隔300ms，增加间隔时间
      }

    } catch (error) {
      message.destroy();
      message.error('生成失败：' + (error as Error).message);
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'teamName',
      key: 'teamName',
    },
    {
      title: '英文名',
      dataIndex: 'englishName',
      key: 'englishName',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '图片',
      dataIndex: 'thumbnailUrl',
      key: 'thumbnailUrl',
      render: (url: string) => <img src={url} alt="card" style={{ width: 100 }} />,
    },
    {
      title: '数量',
      key: 'quantity',
      render: (_: any, record: BackroomsTeamCardInfo) => (
        <Input
          type="number"
          min={0}
          value={quantities[record.id] || 0}
          onChange={(e) => handleQuantityChange(record.id, parseInt(e.target.value) || 0)}
          style={{ width: 80 }}
        />
      ),
    },
  ];

  const filteredData = data.filter(item => 
    item.teamName.toLowerCase().includes(searchText.toLowerCase()) ||
    item.englishName?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="backrooms-team-card">
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="搜索名称或英文名"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Space>
            <Input
              type="number"
              min={0}
              value={bulkQuantity}
              onChange={(e) => setBulkQuantity(parseInt(e.target.value) || 0)}
              style={{ width: 120 }}
              placeholder="批量设置数量"
            />
            <Button onClick={handleBulkQuantityApply}>
              应用到所有
            </Button>
            <Button onClick={handleBulkQuantityApplyToCurrentPage}>
              应用到当前页
            </Button>
            <Button type="primary" onClick={handleGenerateAllTeamCards}>
              生成全部团队卡
            </Button>
          </Space>
        </Space>
      </div>

      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }
        }}
      />

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Button type="primary" onClick={handleBulkSubmit}>
          批量生成
        </Button>
      </div>

      <div style={{ marginTop: 32 }}>
        <h2>创建事件</h2>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEvent}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            <Form.Item
              name="eventName"
              label="事件名称"
              rules={[{ required: true, message: '请输入事件名称' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="startTime"
              label="开始时间"
              rules={[{ required: true, message: '请选择开始时间' }]}
            >
              <Input type="datetime-local" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="endTime"
              label="结束时间"
              rules={[{ required: true, message: '请选择结束时间' }]}
            >
              <Input type="datetime-local" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="requiredContribution"
              label="所需贡献值"
              rules={[{ required: true, message: '请输入所需贡献值' }]}
            >
              <Input type="number" min={0} />
            </Form.Item>

            <Form.Item
              name="experienceReward"
              label="经验奖励"
              rules={[{ required: true, message: '请输入经验奖励' }]}
            >
              <Input type="number" min={0} />
            </Form.Item>

            <Form.Item
              name="levelRequirement"
              label="等级要求"
              rules={[{ required: true, message: '请输入等级要求' }]}
            >
              <Input type="number" min={1} />
            </Form.Item>

            <Form.Item
              name="coverImage"
              label="封面图片"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="allowedTeamIds"
              label="可参与团队"
              rules={[{ required: true, message: '请选择可参与团队' }]}
            >
              <Input placeholder="请输入团队ID，用逗号分隔" />
            </Form.Item>

            <Form.Item
              name="availableAchievementIds"
              label="可获得成就"
            >
              <Input placeholder="请输入成就ID，用逗号分隔" />
            </Form.Item>
            <Form.Item
              name="desc"
              label="描述"
            >
              <Input/>
            </Form.Item>
          </div>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              创建
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default BackroomsTeamCard; 